import { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
    Card,
    CardBody,
    CardHeader,
    CardFooter,
    Button,
    Autocomplete,
    AutocompleteItem,
    DatePicker,
    NumberInput,
    Textarea,
    Spinner,
} from "@heroui/react";
import { LuArrowLeft, LuSave } from "react-icons/lu";
import { usePostData, useFetchData } from "@/hooks";
import { showToast } from "@/utils";
import { ThemedText, PageHeader, ErrorState } from "@/components/ui";
import { parseDate, getLocalTimeZone, today } from "@internationalized/date";

const InitialStockEdit = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    // Unit URL state for dynamic fetching
    const [unitUrl, setUnitUrl] = useState(null);

    // React Hook Form
    const {
        handleSubmit,
        control,
        watch,
        setValue,
        reset,
        formState: { errors },
    } = useForm({
        defaultValues: {
            material_id: "",
            warehouse_id: "",
            material_unit_id: "",
            date: null,
            qty_in: null,
            price: null,
            description: "",
        },
    });

    const watchData = watch();

    // Fetch initial stock data
    const {
        data: initialData,
        isLoading: isFetching,
        error: fetchError,
    } = useFetchData(`stock-initial/${id}/edit`);

    // Fetch dropdown data
    const { data: materials, isLoading: isLoadingMaterials } =
        useFetchData("select/material");
    const { data: warehouses, isLoading: isLoadingWarehouses } =
        useFetchData("select/warehouse");

    // Fetch units based on material_id (dynamic URL)
    const { data: units, isLoading: isLoadingUnits } = useFetchData(unitUrl);

    // Mutation
    const updateMutation = usePostData(`stock-initial/${id}/update`, false);

    // Set unitUrl when initialData is loaded
    useEffect(() => {
        if (initialData?.material_id) {
            setUnitUrl(
                `select/material-unit?material_id=${initialData.material_id}`,
            );
        }
    }, [initialData]);

    // Prefill form when data is loaded
    useEffect(() => {
        if (initialData) {
            reset({
                material_id: initialData.material_id
                    ? String(initialData.material_id)
                    : "",
                warehouse_id: initialData.warehouse_id
                    ? String(initialData.warehouse_id)
                    : "",
                material_unit_id: initialData.material_unit_id
                    ? String(initialData.material_unit_id)
                    : "",
                date: initialData.date || null,
                qty_in: initialData.qty_in || null,
                price: initialData.price || null,
                description: initialData.description || "",
            });
        }
    }, [initialData, reset]);

    // Handle material change
    const handleMaterialChange = (key) => {
        setValue("material_id", key || "");
        setValue("material_unit_id", "");
        if (key) {
            setUnitUrl(`select/material-unit?material_id=${key}`);
        } else {
            setUnitUrl(null);
        }
    };

    // Submit
    const onSubmit = (data) => {
        const payload = {
            material_id: data.material_id,
            warehouse_id: data.warehouse_id,
            material_unit_id: data.material_unit_id,
            date: data.date,
            qty_in: Number(data.qty_in),
            price: data.price ? Number(data.price) : null,
            description: data.description || null,
        };

        updateMutation.mutate(payload, {
            method: "put",
            onSuccess: () => {
                showToast(
                    "success",
                    "Berhasil",
                    "Stok awal berhasil diperbarui.",
                );
                navigate("/stock-initial");
            },
            onError: (err) => {
                let message =
                    "Terjadi kesalahan pada sistem. Silakan coba beberapa saat lagi.";

                if (err.response?.status === 422) {
                    message =
                        "Mohon periksa kembali input Anda. Pastikan semua kolom terisi dengan benar.";
                }

                showToast("error", "Gagal", message);
            },
        });
    };

    // Error state
    if (fetchError) {
        return (
            <ErrorState
                title="Gagal Memuat Data"
                description="Terjadi kesalahan saat memuat data. Silakan coba lagi."
                onAction={() => navigate("/stock-initial")}
                actionLabel="Kembali"
            />
        );
    }

    return (
        <div className="mx-auto flex max-w-6xl flex-col gap-6">
            {/* Header */}
            <div className="flex gap-4">
                <Button isIconOnly variant="light" onPress={() => navigate(-1)}>
                    <LuArrowLeft size={20} />
                </Button>
                <PageHeader
                    title="Edit Stok Awal"
                    breadcrumbs={[
                        { label: "Dashboard", href: "/" },
                        { label: "Stok Awal", href: "/stock-initial" },
                        { label: "Edit" },
                    ]}
                />
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit(onSubmit)} noValidate>
                <Card className="border-default-200 border" shadow="none">
                    <CardHeader className="bg-default-100/70 px-6 py-4">
                        <ThemedText className="font-semibold">
                            Detail Stok Awal
                        </ThemedText>
                    </CardHeader>
                    <CardBody className="p-6">
                        {isFetching ? (
                            <div className="flex h-48 w-full items-center justify-center">
                                <Spinner
                                    label="Memuat data..."
                                    color="primary"
                                />
                            </div>
                        ) : (
                            <div className="flex flex-col gap-4">
                                <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
                                    {/* Left Column: Product Info */}
                                    <div className="flex flex-col gap-4 lg:col-span-5">
                                        <Controller
                                            name="material_id"
                                            control={control}
                                            rules={{
                                                required:
                                                    "Produk wajib dipilih",
                                            }}
                                            render={({ field }) => (
                                                <Autocomplete
                                                    label="Produk"
                                                    placeholder="Cari & pilih produk..."
                                                    isRequired
                                                    isLoading={
                                                        isLoadingMaterials
                                                    }
                                                    selectedKey={
                                                        field.value
                                                            ? String(
                                                                  field.value,
                                                              )
                                                            : null
                                                    }
                                                    onSelectionChange={(
                                                        key,
                                                    ) => {
                                                        field.onChange(
                                                            key || "",
                                                        );
                                                        handleMaterialChange(
                                                            key,
                                                        );
                                                    }}
                                                    variant="bordered"
                                                    isInvalid={
                                                        !!errors.material_id
                                                    }
                                                    errorMessage={
                                                        errors.material_id
                                                            ?.message
                                                    }
                                                    className="max-w-full"
                                                >
                                                    {(materials || []).map(
                                                        (m) => (
                                                            <AutocompleteItem
                                                                key={String(
                                                                    m.value,
                                                                )}
                                                                value={m.value}
                                                            >
                                                                {m.title}
                                                            </AutocompleteItem>
                                                        ),
                                                    )}
                                                </Autocomplete>
                                            )}
                                        />

                                        <div className="grid grid-cols-2 gap-4">
                                            <Controller
                                                name="warehouse_id"
                                                control={control}
                                                rules={{
                                                    required:
                                                        "Gudang wajib dipilih",
                                                }}
                                                render={({ field }) => (
                                                    <Autocomplete
                                                        label="Gudang"
                                                        placeholder="Pilih lokasi"
                                                        isRequired
                                                        isLoading={
                                                            isLoadingWarehouses
                                                        }
                                                        selectedKey={
                                                            field.value
                                                                ? String(
                                                                      field.value,
                                                                  )
                                                                : null
                                                        }
                                                        onSelectionChange={(
                                                            key,
                                                        ) =>
                                                            field.onChange(
                                                                key || "",
                                                            )
                                                        }
                                                        variant="bordered"
                                                        isInvalid={
                                                            !!errors.warehouse_id
                                                        }
                                                        errorMessage={
                                                            errors.warehouse_id
                                                                ?.message
                                                        }
                                                    >
                                                        {(warehouses || []).map(
                                                            (w) => (
                                                                <AutocompleteItem
                                                                    key={String(
                                                                        w.value,
                                                                    )}
                                                                    value={
                                                                        w.value
                                                                    }
                                                                >
                                                                    {w.title}
                                                                </AutocompleteItem>
                                                            ),
                                                        )}
                                                    </Autocomplete>
                                                )}
                                            />

                                            <Controller
                                                name="date"
                                                control={control}
                                                rules={{
                                                    required:
                                                        "Tanggal wajib diisi",
                                                }}
                                                render={({ field }) => (
                                                    <DatePicker
                                                        label="Tanggal"
                                                        isRequired
                                                        showMonthAndYearPickers
                                                        value={
                                                            field.value
                                                                ? parseDate(
                                                                      field.value,
                                                                  )
                                                                : null
                                                        }
                                                        onChange={(date) =>
                                                            field.onChange(
                                                                date
                                                                    ? date.toString()
                                                                    : "",
                                                            )
                                                        }
                                                        maxValue={today(
                                                            getLocalTimeZone(),
                                                        )}
                                                        variant="bordered"
                                                        isInvalid={
                                                            !!errors.date
                                                        }
                                                        errorMessage={
                                                            errors.date?.message
                                                        }
                                                    />
                                                )}
                                            />
                                        </div>
                                    </div>

                                    {/* Right Column: Qty & Price */}
                                    <div className="flex flex-col gap-4 lg:col-span-7">
                                        <div className="grid grid-cols-3 gap-4">
                                            <div className="col-span-2">
                                                <Controller
                                                    name="qty_in"
                                                    control={control}
                                                    rules={{
                                                        required:
                                                            "Qty wajib diisi",
                                                        min: {
                                                            value: 1,
                                                            message:
                                                                "Qty harus lebih dari 0",
                                                        },
                                                    }}
                                                    render={({ field }) => (
                                                        <NumberInput
                                                            label="Qty"
                                                            placeholder="Contoh: 100"
                                                            isRequired
                                                            isClearable
                                                            value={
                                                                field.value ??
                                                                undefined
                                                            }
                                                            onValueChange={(
                                                                val,
                                                            ) =>
                                                                field.onChange(
                                                                    val,
                                                                )
                                                            }
                                                            variant="bordered"
                                                            minValue={0}
                                                            hideStepper
                                                            isWheelDisabled
                                                            isInvalid={
                                                                !!errors.qty_in
                                                            }
                                                            errorMessage={
                                                                errors.qty_in
                                                                    ?.message
                                                            }
                                                        />
                                                    )}
                                                />
                                            </div>
                                            <Controller
                                                name="material_unit_id"
                                                control={control}
                                                rules={{
                                                    required:
                                                        "Satuan wajib dipilih",
                                                }}
                                                render={({ field }) => (
                                                    <Autocomplete
                                                        label="Satuan"
                                                        placeholder={
                                                            watchData.material_id
                                                                ? "Pilih satuan"
                                                                : "Pilih produk dulu"
                                                        }
                                                        isRequired
                                                        isDisabled={
                                                            !watchData.material_id
                                                        }
                                                        isLoading={
                                                            isLoadingUnits
                                                        }
                                                        selectedKey={
                                                            field.value
                                                                ? String(
                                                                      field.value,
                                                                  )
                                                                : null
                                                        }
                                                        onSelectionChange={(
                                                            key,
                                                        ) =>
                                                            field.onChange(
                                                                key || "",
                                                            )
                                                        }
                                                        variant="bordered"
                                                        isInvalid={
                                                            !!errors.material_unit_id
                                                        }
                                                        errorMessage={
                                                            errors
                                                                .material_unit_id
                                                                ?.message
                                                        }
                                                    >
                                                        {(units || []).map(
                                                            (u) => (
                                                                <AutocompleteItem
                                                                    key={String(
                                                                        u.value,
                                                                    )}
                                                                    value={
                                                                        u.value
                                                                    }
                                                                >
                                                                    {u.title}
                                                                </AutocompleteItem>
                                                            ),
                                                        )}
                                                    </Autocomplete>
                                                )}
                                            />
                                        </div>

                                        <div className="w-full">
                                            <Controller
                                                name="price"
                                                control={control}
                                                render={({ field }) => (
                                                    <NumberInput
                                                        label="Harga Beli (Satuan)"
                                                        placeholder="0"
                                                        value={
                                                            field.value ??
                                                            undefined
                                                        }
                                                        onValueChange={(val) =>
                                                            field.onChange(val)
                                                        }
                                                        variant="bordered"
                                                        minValue={0}
                                                        hideStepper
                                                        startContent={
                                                            <ThemedText
                                                                size="sm"
                                                                variant="secondary"
                                                            >
                                                                Rp
                                                            </ThemedText>
                                                        }
                                                        formatOptions={{
                                                            style: "decimal",
                                                            minimumFractionDigits: 0,
                                                        }}
                                                    />
                                                )}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Keterangan Full Width Row */}
                                <Controller
                                    name="description"
                                    control={control}
                                    render={({ field }) => (
                                        <Textarea
                                            label="Keterangan"
                                            placeholder="Keterangan tambahan (opsional)"
                                            value={field.value || ""}
                                            onValueChange={(val) =>
                                                field.onChange(val)
                                            }
                                            variant="bordered"
                                            className="w-full"
                                        />
                                    )}
                                />
                            </div>
                        )}
                    </CardBody>
                    <CardFooter className="flex justify-end gap-3 px-6 py-4">
                        <Button
                            as={Link}
                            href="/stock-initial"
                            type="button"
                            variant="flat"
                            size="sm"
                        >
                            Batal
                        </Button>
                        <Button
                            color="primary"
                            type="submit"
                            isLoading={updateMutation.isLoading}
                            startContent={<LuSave size={16} />}
                            size="sm"
                        >
                            Simpan Perubahan
                        </Button>
                    </CardFooter>
                </Card>
            </form>
        </div>
    );
};

export default InitialStockEdit;
